import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Smartphone, QrCode, Power, PowerOff, RefreshCw, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { useWhatsAppStatus, useInitializeWhatsApp, useDisconnectWhatsApp } from '@/hooks/useWhatsAppStatus';
import { useToast } from '@/hooks/use-toast';

export const WhatsAppStatusCard: React.FC = () => {
  const { data: status, isLoading, refetch } = useWhatsAppStatus();
  const initializeWhatsApp = useInitializeWhatsApp();
  const disconnectWhatsApp = useDisconnectWhatsApp();
  const { toast } = useToast();

  const handleInitialize = async () => {
    try {
      await initializeWhatsApp.mutateAsync();
      toast({
        title: 'Success',
        description: 'WhatsApp service initialized successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to initialize WhatsApp service',
        variant: 'destructive',
      });
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectWhatsApp.mutateAsync();
      toast({
        title: 'Success',
        description: 'WhatsApp service disconnected successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to disconnect WhatsApp service',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = () => {
    if (isLoading) {
      return (
        <Badge variant="outline" className="bg-gray-100 text-gray-800">
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          Loading...
        </Badge>
      );
    }

    if (status?.isReady) {
      return (
        <Badge className="bg-green-100 text-green-800">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Connected
        </Badge>
      );
    }

    if (status?.isInitialized && !status?.isReady) {
      return (
        <Badge className="bg-yellow-100 text-yellow-800">
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          Initializing
        </Badge>
      );
    }

    return (
      <Badge className="bg-red-100 text-red-800">
        <XCircle className="w-3 h-3 mr-1" />
        Disconnected
      </Badge>
    );
  };

  const formatUptime = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Smartphone className="h-5 w-5" />
            <span>WhatsApp Status</span>
          </CardTitle>
          <Button
            onClick={() => refetch()}
            variant="outline"
            size="sm"
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <div
              className={`w-3 h-3 rounded-full ${
                status?.isReady ? 'bg-green-500' : 'bg-red-500'
              }`}
            ></div>
            <span className="text-sm font-medium">Connection</span>
            {getStatusBadge()}
          </div>

          {status?.phoneNumber && (
            <div className="flex items-center space-x-3">
              <Smartphone className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">Phone</span>
              <span className="text-sm text-gray-600">{status.phoneNumber}</span>
            </div>
          )}

          {status?.uptime && (
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium">Uptime</span>
              <span className="text-sm text-gray-600">{formatUptime(status.uptime)}</span>
            </div>
          )}
        </div>

        {/* QR Code Display */}
        {status?.hasQR && status?.currentQR && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-2 mb-3">
              <QrCode className="h-5 w-5 text-blue-600" />
              <h3 className="text-sm font-semibold text-gray-700">
                Scan QR Code to Authenticate
              </h3>
            </div>
            <div className="flex justify-center p-4 bg-white rounded-md">
              <img
                src={status.currentQR}
                alt="WhatsApp QR Code"
                className="w-48 h-48 border-2 border-gray-300 rounded-lg"
              />
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">
              Open WhatsApp on your phone and scan this QR code to authenticate
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          {!status?.isInitialized ? (
            <Button
              onClick={handleInitialize}
              disabled={initializeWhatsApp.isPending}
              className="flex-1"
            >
              {initializeWhatsApp.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Initializing...
                </>
              ) : (
                <>
                  <Power className="w-4 h-4 mr-2" />
                  Initialize WhatsApp
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleDisconnect}
              disabled={disconnectWhatsApp.isPending}
              variant="destructive"
              className="flex-1"
            >
              {disconnectWhatsApp.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                <>
                  <PowerOff className="w-4 h-4 mr-2" />
                  Disconnect
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
